import {
    Box,
    Button,
    CircularProgress,
    Container,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import KeyIcon from '@mui/icons-material/Key';
import BusinessIcon from '@mui/icons-material/Business';
import DashboardCard from "components/dashboard/common/DashboardCard";
import UserContext from "Context/UserProvider";
import { ElectricVehicle } from "interfaces/ElectricVehicle";
import { useContext, useEffect, useState } from "react";
import toast from 'react-hot-toast';
import { useNavigate } from "react-router-dom";
import { useApiService } from "../../services/ApiService/ApiService";

function ElectricVehicleEditPage(props: any) {
    const defaultElectricVehicle: ElectricVehicle = {
        uuid: null,
        registrationNumber: null,
        vin: null,
        username: null,
        password: null,
        ownershipStatus: "FLEET"
    };

    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const { userInfo } = useContext(UserContext);
    const { fetchWithAuth } = useApiService();
    const [electricVehicle, setElectricVehicle] = useState<ElectricVehicle>(defaultElectricVehicle);
    const [isDisabled, setIsDisabled] = useState(false);

    const ownershipStatusOptions = [
        { value: 'FLEET', label: 'Fleet' },
        { value: 'RENTAL', label: 'Rental' },
    ];

    useEffect(() => {
        setElectricVehicle(defaultElectricVehicle);
        setLoading(false);
    }, []);

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setElectricVehicle(prev => ({
            ...prev,
            [name]: value
        }));
    };

    async function updateElectricVehicle() {
        try {
            setIsDisabled(true);
            const bodyData = {
                uuid: userInfo.uuid,
                electricVehicle,
                businessUuid: userInfo.businessUuid
            };

            const response = await fetchWithAuth('/fleet/electric-vehicle/save', {
                method: 'POST',
                body: JSON.stringify(bodyData),
            });

            if (response.ok) {
                toast.success('Electric Vehicle Saved', {
                    position: 'bottom-center',
                    duration: 5000,
                });
                navigate("/share/business/electric-vehicles");
            } else {
                toast.error('Failed to save Electric Vehicle');
            }
        } catch (error) {
            console.error('Error saving electric vehicle:', error);
            toast.error('An error occurred while saving');
        } finally {
            setIsDisabled(false);
        }
    }

    return loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
        </Box>
    ) : (
        <Container maxWidth="xl">
            <Box pr={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6} lg={4}>
                        <DashboardCard
                            title="Vehicle Details"
                            icon={<DirectionsCarIcon />}
                            accentColor="primary"
                        >
                            <Stack spacing={3}>
                                <TextField
                                    fullWidth
                                    label="Vehicle Registration Number"
                                    name="registrationNumber"
                                    value={electricVehicle.registrationNumber ?? ''}
                                    onChange={handleChange}
                                />
                                <TextField
                                    fullWidth
                                    label="VIN"
                                    name="vin"
                                    value={electricVehicle.vin ?? ''}
                                    onChange={handleChange}
                                />
                            </Stack>
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                        <DashboardCard
                            title="Authorization"
                            icon={<KeyIcon />}
                            accentColor="primary"
                        >
                            <Stack spacing={3}>
                                <TextField
                                    fullWidth
                                    label="Username"
                                    name="username"
                                    value={electricVehicle.username ?? ''}
                                    onChange={handleChange}
                                />
                                <TextField
                                    fullWidth
                                    type="password"
                                    label="Password"
                                    name="password"
                                    value={electricVehicle.password ?? ''}
                                    onChange={handleChange}
                                />
                            </Stack>
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                        <DashboardCard
                            title="Ownership"
                            icon={<BusinessIcon />}
                            accentColor="primary"
                        >
                            <FormControl fullWidth>
                                <InputLabel>Ownership Status</InputLabel>
                                <Select
                                    value={electricVehicle.ownershipStatus}
                                    name="ownershipStatus"
                                    label="Ownership Status"
                                    onChange={handleChange}
                                >
                                    {ownershipStatusOptions.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center">
                            <Button
                                variant="contained"
                                onClick={updateElectricVehicle}
                                disabled={isDisabled}
                            >
                                Save
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
}

export default ElectricVehicleEditPage;
